'use client';

import type { PaymentMethodLogosBlockConfig } from '@/cms/blocks/catalog/pricing/payment-methods-1';
import { CmsEditorialIcon } from '@/cms/components/blocks/shared/cms-editorial-icon';
import { CmsSectionShell } from '@/cms/components/blocks/shared/cms-section-shell';
import { CmsOverlayDialogContent } from '@/cms/components/overlays/cms-overlay-dialog-content';
import {
  Dialog,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/overlay/dialog';
import { useState } from 'react';

type PaymentMethodLogo = {
  alt: string;
  dialogSrc: string;
  name: string;
  src: string;
};

type FeaturedPaymentMethodLogo = PaymentMethodLogo & {
  featuredHeight: number;
  featuredWidth: number;
};

const paymentMethodLogo = (
  name: string,
  fileName: string,
  dialogFileName = fileName
): PaymentMethodLogo => ({
  alt: name,
  dialogSrc: `/svg/payment-method/${dialogFileName}.svg`,
  name,
  src: `/svg/payment-method/${fileName}.svg`,
});

const PAYMENT_METHOD_LOGOS = {
  americanExpress: paymentMethodLogo('American Express', 'american-express'),
  applePay: paymentMethodLogo('Apple Pay', 'apple-pay'),
  bancontact: paymentMethodLogo('Bancontact', 'bancontact'),
  cartesBancaires: paymentMethodLogo('Cartes Bancaires', 'cartes-bancaires'),
  cashAppPay: paymentMethodLogo('Cash App Pay', 'cash-app-pay'),
  clickToPay: paymentMethodLogo(
    'Click to Pay',
    'click-to-pay',
    'click-to-pay-on-light'
  ),
  dinersClub: paymentMethodLogo('Diners Club', 'diners-club'),
  dana: paymentMethodLogo('DANA', 'dana'),
  discover: paymentMethodLogo('Discover', 'discover', 'discover-on-light'),
  fonix: paymentMethodLogo('Fonix', 'fonix'),
  googlePay: paymentMethodLogo('Google Pay', 'google-pay'),
  ideal: paymentMethodLogo('iDEAL', 'ideal'),
  jcb: paymentMethodLogo('JCB', 'jcb'),
  konbini: paymentMethodLogo('Konbini', 'konbini'),
  link: paymentMethodLogo('Link', 'link'),
  mastercard: paymentMethodLogo(
    'Mastercard',
    'mastercard',
    'mastercard-on-light'
  ),
  merpay: paymentMethodLogo('MerPay', 'merpay'),
  postepay: paymentMethodLogo('Postepay', 'postepay'),
  postfinance: paymentMethodLogo('PostFinance', 'postfinance'),
  paypal: paymentMethodLogo('PayPal', 'paypal'),
  rakutenPay: paymentMethodLogo('Rakuten Pay', 'rakuten-pay'),
  revolutPay: paymentMethodLogo('Revolut Pay', 'revolut-pay'),
  sberpay: paymentMethodLogo('SberPay', 'sberpay'),
  sepaDirectDebit: paymentMethodLogo('SEPA Direct Debit', 'sepa-direct-debit'),
  spei: paymentMethodLogo('SPEI', 'spei'),
  tbank: paymentMethodLogo('T-Bank', 'tbank'),
  touchNGo: paymentMethodLogo("Touch'n Go", 'touch-n-go'),
  truelayer: paymentMethodLogo('TrueLayer', 'truelayer'),
  unionPay: paymentMethodLogo('UnionPay', 'unionpay'),
  visa: paymentMethodLogo('Visa', 'visa'),
  yoomoney: paymentMethodLogo('YooMoney', 'yoomoney'),
} satisfies Record<string, PaymentMethodLogo>;

const FEATURED_PAYMENT_METHODS = [
  { ...PAYMENT_METHOD_LOGOS.visa, featuredHeight: 28, featuredWidth: 84 },
  {
    ...PAYMENT_METHOD_LOGOS.mastercard,
    featuredHeight: 32,
    featuredWidth: 179,
  },
  {
    ...PAYMENT_METHOD_LOGOS.americanExpress,
    featuredHeight: 28,
    featuredWidth: 99,
  },
  { ...PAYMENT_METHOD_LOGOS.paypal, featuredHeight: 28, featuredWidth: 105 },
  {
    ...PAYMENT_METHOD_LOGOS.applePay,
    featuredHeight: 28,
    featuredWidth: 44,
  },
  {
    ...PAYMENT_METHOD_LOGOS.googlePay,
    featuredHeight: 28,
    featuredWidth: 53,
  },
  {
    ...PAYMENT_METHOD_LOGOS.discover,
    featuredHeight: 28,
    featuredWidth: 158,
  },
  {
    ...PAYMENT_METHOD_LOGOS.unionPay,
    featuredHeight: 28,
    featuredWidth: 45,
  },
] satisfies readonly FeaturedPaymentMethodLogo[];

type PaymentMethodRegionTitleKey =
  | 'northAmericaTitle'
  | 'europeTitle'
  | 'latinAmericaTitle'
  | 'asiaPacificTitle'
  | 'middleEastAfricaTitle';

const PAYMENT_METHOD_REGIONS: ReadonlyArray<{
  logos: readonly PaymentMethodLogo[];
  titleKey: PaymentMethodRegionTitleKey;
}> = [
  {
    titleKey: 'northAmericaTitle',
    logos: [
      PAYMENT_METHOD_LOGOS.visa,
      PAYMENT_METHOD_LOGOS.mastercard,
      PAYMENT_METHOD_LOGOS.americanExpress,
      PAYMENT_METHOD_LOGOS.discover,
      PAYMENT_METHOD_LOGOS.dinersClub,
      PAYMENT_METHOD_LOGOS.clickToPay,
      PAYMENT_METHOD_LOGOS.applePay,
      PAYMENT_METHOD_LOGOS.googlePay,
      PAYMENT_METHOD_LOGOS.link,
      PAYMENT_METHOD_LOGOS.cashAppPay,
    ],
  },
  {
    titleKey: 'europeTitle',
    logos: [
      PAYMENT_METHOD_LOGOS.visa,
      PAYMENT_METHOD_LOGOS.mastercard,
      PAYMENT_METHOD_LOGOS.americanExpress,
      PAYMENT_METHOD_LOGOS.discover,
      PAYMENT_METHOD_LOGOS.dinersClub,
      PAYMENT_METHOD_LOGOS.clickToPay,
      PAYMENT_METHOD_LOGOS.applePay,
      PAYMENT_METHOD_LOGOS.googlePay,
      PAYMENT_METHOD_LOGOS.link,
      PAYMENT_METHOD_LOGOS.sepaDirectDebit,
      PAYMENT_METHOD_LOGOS.ideal,
      PAYMENT_METHOD_LOGOS.bancontact,
      PAYMENT_METHOD_LOGOS.cartesBancaires,
      PAYMENT_METHOD_LOGOS.postepay,
      PAYMENT_METHOD_LOGOS.postfinance,
      PAYMENT_METHOD_LOGOS.truelayer,
      PAYMENT_METHOD_LOGOS.revolutPay,
      PAYMENT_METHOD_LOGOS.fonix,
      PAYMENT_METHOD_LOGOS.tbank,
      PAYMENT_METHOD_LOGOS.yoomoney,
      PAYMENT_METHOD_LOGOS.sberpay,
    ],
  },
  {
    titleKey: 'latinAmericaTitle',
    logos: [
      PAYMENT_METHOD_LOGOS.visa,
      PAYMENT_METHOD_LOGOS.mastercard,
      PAYMENT_METHOD_LOGOS.americanExpress,
      PAYMENT_METHOD_LOGOS.clickToPay,
      PAYMENT_METHOD_LOGOS.applePay,
      PAYMENT_METHOD_LOGOS.googlePay,
      PAYMENT_METHOD_LOGOS.spei,
    ],
  },
  {
    titleKey: 'asiaPacificTitle',
    logos: [
      PAYMENT_METHOD_LOGOS.visa,
      PAYMENT_METHOD_LOGOS.mastercard,
      PAYMENT_METHOD_LOGOS.americanExpress,
      PAYMENT_METHOD_LOGOS.discover,
      PAYMENT_METHOD_LOGOS.dinersClub,
      PAYMENT_METHOD_LOGOS.clickToPay,
      PAYMENT_METHOD_LOGOS.applePay,
      PAYMENT_METHOD_LOGOS.googlePay,
      PAYMENT_METHOD_LOGOS.link,
      PAYMENT_METHOD_LOGOS.jcb,
      PAYMENT_METHOD_LOGOS.unionPay,
      PAYMENT_METHOD_LOGOS.dana,
      PAYMENT_METHOD_LOGOS.rakutenPay,
      PAYMENT_METHOD_LOGOS.merpay,
      PAYMENT_METHOD_LOGOS.konbini,
      PAYMENT_METHOD_LOGOS.touchNGo,
    ],
  },
  {
    titleKey: 'middleEastAfricaTitle',
    logos: [
      PAYMENT_METHOD_LOGOS.visa,
      PAYMENT_METHOD_LOGOS.mastercard,
      PAYMENT_METHOD_LOGOS.americanExpress,
      PAYMENT_METHOD_LOGOS.clickToPay,
      PAYMENT_METHOD_LOGOS.applePay,
      PAYMENT_METHOD_LOGOS.googlePay,
    ],
  },
];

const blockClass = 'cms-block-pricing-payment-methods-1-payment-methods-1';

export function PaymentMethodLogosBlock({
  config,
}: {
  config: PaymentMethodLogosBlockConfig;
}) {
  const [open, setOpen] = useState(false);

  return (
    <CmsSectionShell
      className={`${blockClass}__section`}
      innerClassName={`${blockClass}__inner`}
      width="tight"
    >
      <div className={`${blockClass}__content`} data-payment-method-logos>
        <p className={`${blockClass}__eyebrow`}>
          <CmsEditorialIcon
            className={`${blockClass}__eyebrowIcon`}
            name="shield-check"
          />
          {config.eyebrow}
        </p>

        <div className={`${blockClass}__featured`}>
          <div className={`${blockClass}__featuredLogos`}>
            {FEATURED_PAYMENT_METHODS.map((logo) => (
              <span
                className={`${blockClass}__featuredCard`}
                data-payment-method-featured-logo={logo.name}
                key={logo.name}
                style={{
                  aspectRatio: `${logo.featuredWidth} / ${logo.featuredHeight}`,
                }}
              >
                <img
                  alt={logo.alt}
                  className={`${blockClass}__featuredLogo`}
                  height={logo.featuredHeight}
                  src={logo.src}
                  width={logo.featuredWidth}
                />
              </span>
            ))}
          </div>
          <button
            aria-expanded={open}
            className={`${blockClass}__more`}
            onClick={() => setOpen(true)}
            type="button"
          >
            {config.moreLabel}
          </button>
        </div>
      </div>

      <Dialog onOpenChange={setOpen} open={open}>
        <CmsOverlayDialogContent
          className={`${blockClass}__dialog`}
          positionerClassName={`${blockClass}__dialogPositioner`}
        >
          <DialogTitle className="sr-only">{config.dialogTitle}</DialogTitle>
          <DialogDescription className="sr-only">
            {config.dialogDescription}
          </DialogDescription>

          <div className={`${blockClass}__dialogBody`}>
            <div className={`${blockClass}__dialogScroll`}>
              <ul className={`${blockClass}__regionList`}>
                {PAYMENT_METHOD_REGIONS.map((region) => (
                  <li className={`${blockClass}__region`} key={region.titleKey}>
                    <h3 className={`${blockClass}__regionTitle`}>
                      {config[region.titleKey]}
                    </h3>
                    <div className={`${blockClass}__regionGrid`}>
                      {region.logos.map((logo) => (
                        <span
                          className={`${blockClass}__regionCard`}
                          data-payment-method-logo-card
                          key={`${region.titleKey}-${logo.name}`}
                        >
                          <img
                            alt={logo.alt}
                            className={`${blockClass}__regionLogo`}
                            height="20"
                            loading="lazy"
                            src={logo.dialogSrc}
                            width="120"
                          />
                        </span>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CmsOverlayDialogContent>
      </Dialog>
    </CmsSectionShell>
  );
}
